import React, { useEffect, useState } from 'react';
import colors from 'theme/_colors.scss';
import { getTranslatedHistoScatterV2KPIName, translate } from 'helpers/translateHelper';
import { CHART_PLUGINS } from 'sharedComponents/Chart/constants';
import { defaultBoostThreshold, firstDataColor, ASSET_TYPES_MS } from 'helpers/constants';
import { renderToStaticMarkup } from 'react-dom/server';
import ScatterTooltip from './ScatterTooltip';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { RemovableLegend } from 'sharedComponents/ChartComponents/CommonLegend';
import { afterChartReDraw, chartLegendItemClick } from '../../../../helpers';
import { cloneDeep } from 'lodash';
import { setMaxPrecision } from 'helpers/formatters';


export const chartPlugins = [CHART_PLUGINS.HIGHCHARTS_MORE, CHART_PLUGINS.NO_DATA_TO_DISPLAY, CHART_PLUGINS.BOOST];

const useChartConfig = (data, scatterConfig, scatterKpis, removeFn, asset) => {
    const [options, setOptions] = useState({});
    const scatterNames = scatterConfig ? scatterConfig?.name?.split(' vs ') : [];
    const xName = scatterNames.length > 1 && asset?.type === ASSET_TYPES_MS.DRIVE ? scatterNames[1] : scatterNames[0];
    const yName = scatterNames.length > 1 && asset?.type === ASSET_TYPES_MS.DRIVE ? scatterNames[0] : scatterNames[1];
    const updatedOffsetData = (data) => {
        return data?.map((point) => {
            if (scatterConfig.xOffset) {
                point.x = setMaxPrecision(point.x + scatterConfig.xOffset, scatterConfig?.xPrecision ?? scatterConfig?.precision);
            }
            if (scatterConfig.yOffset) {
                point.y = setMaxPrecision(point.y + scatterConfig.yOffset, scatterConfig?.yPrecision ?? scatterConfig?.precision);
            }
            return point;
        });
    };

    useEffect(() => {
        const { yUnit, xUnit } = scatterConfig || {};
        const name = scatterConfig ? getTranslatedHistoScatterV2KPIName(scatterConfig) : null;
        const id = scatterKpis.findIndex((item) => item.name === name);
        data = updatedOffsetData(cloneDeep(data || []));
        setOptions({
            chart: {
                type: 'scatter',
                className: 'chartstyle',
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
                zoomType: 'xy',
                height: null,
                events: {
                    displayError: function (event) {
                        // https://github.com/highcharts/highcharts/issues/14006
                        if (event.code === 12) {
                            event.preventDefault();
                        }
                    },
                    render: function (chartInstance) {
                        afterChartReDraw(chartInstance.target.container.id, removeFn);
                    }
                }
            },
            boost: {
                useGPUTranslations: true,
                usePreAllocated: true
            },
            title: {
                text: ''
            },
            xAxis: {
                title: {
                    enabled: true,
                    text: `${xName} ${xUnit ? `(${xUnit})` : ''}`
                },
                startOnTick: true,
                endOnTick: true,
                showLastLabel: true
            },
            yAxis: {
                title: {
                    enabled: true,
                    text: `${yName} ${yUnit ? `(${yUnit})` : ''}`
                }
            },
            plotOptions: {
                scatter: {
                    marker: {
                        radius: 3,
                        states: {
                            hover: {
                                enabled: true,
                                lineColor: colors.doveGray
                            }
                        }
                    },
                    states: {
                        hover: {
                            marker: {
                                enabled: false
                            }
                        }
                    },
                    boostThreshold: defaultBoostThreshold
                },
                series: {
                    events: {
                        legendItemClick: chartLegendItemClick
                    }
                }
            },
            tooltip: {
                useHTML: true,
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                formatter: function () {
                    return renderToStaticMarkup(
                        <ScatterTooltip
                            xUnit={xUnit}
                            yUnit={yUnit}
                            xName={xName}
                            yName={yName}
                            point={this.point}
                        />);
                }
            },
            lang: {
                noData: renderToStaticMarkup(<ChartNoDataText
                    title={translate('ABB.Powertrain.Frontend.noDataLabelAvailable')}
                    text={translate('ABB.Powertrain.Frontend.adjustDateRangeOrChartType')} />)

            },
            noData: {
                useHTML: true
            },
            series: data.length ? [
                {
                    data,
                    name,
                    id,
                    color: firstDataColor,
                    turboThreshold: 0
                }
            ] : [],
            legend: {
                useHTML: true,
                layout: 'horizontal',
                align: 'right',
                itemStyle: {
                    fontFamily: 'ABBvoice-Regular',
                    fontSize: '14px',
                },
                itemDistance: 25,
                symbolHeight: 8,
                symbolWidth: 8,
                symbolRadius: 0,
                itemMarginTop: 10,
                labelFormatter: function () {
                    return renderToStaticMarkup(<RemovableLegend
                        signalSourceName={this.userOptions.component}
                        legend={this}
                        className='cross-asset__legend'
                    />);
                }
            },
            credits: {
                enabled: false
            },
            navigation: {
                buttonOptions: {
                    theme: {
                        states: {
                            hover: {
                                fill: colors.white,
                                stroke: 'none'
                            },
                            select: {
                                fill: colors.white,
                                select: 'none'
                            }
                        }
                    }
                }
            },
            exporting: {
                enabled: false,
            }
        });
    }, [data]);

    return options;
};

export default useChartConfig;
